import { FocusMonitor } from '@angular/cdk/a11y';
import * as KeyCode from '@angular/cdk/keycodes';
import { AfterContentInit, AfterViewChecked, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Host, HostListener, Input, OnChanges, OnDestroy, Optional, SimpleChanges, SkipSelf, ViewChild } from '@angular/core';
import { FormControl, FormGroupDirective } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { BehaviorSubject, Observable, Subject, Subscription, throwError } from 'rxjs';
import { catchError, debounceTime, skip, take, tap } from 'rxjs/operators';
import { NebulaDataSource } from '../nebula-table/nebula-data-source';

@Component({
  selector: 'nebula-autocomplete',
  templateUrl: './nebula-autocomplete.component.html',
  styleUrls: ['./nebula-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NebulaAutocompleteComponent implements OnDestroy, OnChanges, AfterContentInit, AfterViewChecked {

  @Input() public control: FormControl;
  @Input() public compareItems: Array<string> = [];
  @Input() public name: string;
  @Input() public setFocus = false;
  @Input() public multiple = true;
  @Input() public dataSource: NebulaDataSource<any>;
  @Input() public hasGroups = false;

  @ViewChild('itemInput', { static: false }) public itemInput: ElementRef;
  @ViewChild('dropdown', { static: false }) public dropdown: MatMenu;
  @ViewChild(MatMenuTrigger, { static: false }) public menuTrigger: MatMenuTrigger;
  @ViewChild(MatAutocompleteTrigger, { static: false }) public trigger: MatAutocompleteTrigger;
  @ViewChild(MatFormField, { static: false }) public matFormField: MatFormField;
  @ViewChild(MatButton, { static: false }) public button: MatButton;
  @ViewChild('content', { static: false }) menuElement: ElementRef;

  @ViewChild(MatSelectionList, { static: false }) matSelectionList: MatSelectionList;

  viewChange: BehaviorSubject<{ start: number, end: number }> =
    new BehaviorSubject<{ start: number, end: number }>({ start: 0, end: Number.MAX_VALUE });

  public inputControl: FormControl;

  public showHintMessage = false;

  public displayedListSubject: Subject<any[]>;
  public displayedList: Observable<any[]>;
  public filteredList: Observable<any[]>;
  public lastValue: string;

  _inputSubscription: Subscription;
  _filterSubscription: Subscription;
  _menuOpenSubscription: Subscription;
  _menuCloseSubscription: Subscription;
  _overlaySubscription: Subscription;

  public disabled = false;

  public label: string;
  public showSpinner = false;
  public showError = false;
  private _scrolling = false;
  private _lastValues: any[] = [];

  public selectableOptionIndex = 0;

  constructor(
    @Host() @SkipSelf() @Optional() private _formGroup: FormGroupDirective,
    private monitor: FocusMonitor,
    private cdRef: ChangeDetectorRef
  ) { }

  @HostListener('window:resize')
  onResize() {
    setTimeout(() => {
      this._setDropdownWidth();
    });
  }

  ngAfterContentInit() {
    this.inputControl = new FormControl(null, () => this.control.valid ? null : { error: true });
    this.displayedListSubject = new Subject<any[]>();
    this.displayedList = this.displayedListSubject.asObservable();

    this.showSpinner = !!this.multiple;

    if (this.multiple && !this.control.value) {
      this.control.setValue([]);
    }
    if (!this.multiple && this.control.value && typeof this.control.value !== 'string') {
      this.inputControl.setValue(this.control.value);
    }
    if (this.dataSource) {
      this._initSubscriptions();
      setTimeout(() => {
        if (this.trigger) {
          this.trigger._handleFocus = () => {
            this.triggerChange(true);
          };
        }
      });
    }
  }

  ngOnDestroy() {
    if (this._inputSubscription) {
      this._inputSubscription.unsubscribe();
    }
    if (this._filterSubscription) {
      this._filterSubscription.unsubscribe();
    }
    if (this.dataSource) {
      this.dataSource.disconnect(this);
    }
    if (this.displayedListSubject) {
      this.displayedListSubject.complete();
    }
    if (this._menuOpenSubscription) {
      this._menuOpenSubscription.unsubscribe();
    }
    if (this._menuCloseSubscription) {
      this._menuCloseSubscription.unsubscribe();
    }
  }

  private _initSubscriptions() {
    this.filteredList = this.dataSource.connect(this);
    this._inputSubscription = this.inputControl.valueChanges.pipe(
      tap(() => this.showSpinner = true),
      tap(() => {
        this.displayedListSubject.next([]);
      }),
      debounceTime(300)
    ).subscribe(value => {
      this.lastValue = value;
      this._filterValue(value);
    });

    this._filterSubscription = this.filteredList.pipe(skip(1)).subscribe((fl: Array<any>) => {
      if (this.inputControl.value && !this._scrolling) {
        this._lastValues = fl;
      } else {
        this._lastValues = (this._lastValues || []).concat(fl);
      }
      this.displayedListSubject.next(this._lastValues);
      this._scrolling = false;
      this.showSpinner = false;
      this.showError = false;

      if (this.multiple) {
        this.showHintMessage = this._lastValues.length === 0;
      } else {
        this.showHintMessage = this._lastValues.length === 0 && this.inputControl.value !== '';
      }
      this._dispatchWindowResizeEvent();
    });

    if (this.multiple) {
      setTimeout(() => {

        if (this.matFormField) {
          this.label = (this.matFormField as any)._label.nativeElement.innerText;
        }

        if (this.menuTrigger) {
          this._menuOpenSubscription = this.menuTrigger.menuOpened.subscribe(() => {
            this.handleDropdown();
          });
          this._menuCloseSubscription = this.menuTrigger.menuClosed.subscribe(() => {
            this.monitor.focusVia(this.button._elementRef, 'keyboard');
          });
        }
      });
    }
  }

  _filterValue(value: any) {
    let filterValue = '';
    if (typeof value === 'string') {
      filterValue = value;
    } else if (value) {
      filterValue = value.getDisplayName();
    }

    let criteria = `display_name.contains('${NebulaDataSource.escapeFilterValue(filterValue)}')`;
    if (this.compareItems.length > 0) {
      const stringValues = this.compareItems.map((v: any) => `'${NebulaDataSource.escapeFilterValue(v)}'`).join(',');
      criteria += ` && !(name in [${stringValues}])`;
    }

    this._lastValues = [];

    this.dataSource.firstPage(criteria, '').pipe(
      take(1),
      catchError((err) => {
        this.showSpinner = false;
        this.showError = true;
        return throwError(err);
      })
    ).subscribe(() => this.selectableOptionIndex = 0);
  }

  triggerChange(closePanel?: boolean) {
    if (this.multiple) {
      this.itemInput.nativeElement.value = '';
      this.inputControl.setValue('', { onlySelf: true });
    } else {
      this.triggerSimpleChange(closePanel);
    }
  }

  triggerSimpleChange(closePanel?: boolean) {
    this.itemInput.nativeElement.value = this.itemInput.nativeElement.value || '';
    this.inputControl.setValue(this.control.valid ? this.control.value : '', { onlySelf: true });
  }

  displayName(item?: any): string | undefined {
    return item && item.getDisplayName ? item.getDisplayName() : undefined;
  }

  addSelection($event: MatAutocompleteSelectedEvent) {
    const name = $event.option.value.getName().substr($event.option.value.getName().lastIndexOf('/'));
    const valueIndex = this.multiple ? this.control.value.findIndex((v: any) => v.getName().indexOf(name) > -1) : null;
    this.control.markAsDirty();
    if (this.multiple) {
      if (valueIndex === -1) {
        this.control.value.push($event.option.value);
        this.control.updateValueAndValidity();
      }
    } else if (!this.multiple) {
      this.control.setValue($event.option.value);
      this.triggerChange(true);
    }
  }

  removeSelection(index: number, change = true) {
    this.control.value.splice(index, 1);
    this.control.updateValueAndValidity();
    this.control.markAsDirty();
    if (change) {
      this.triggerChange();
    }
    if (this.multiple && this.control.value.length === 0) {
      this.focusOnExpand();
    }
    this.cdRef.markForCheck();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.setFocus && changes.setFocus.currentValue &&
      this.itemInput && this.itemInput.nativeElement && !this.button) {
      this.monitor.focusVia(this.itemInput.nativeElement, 'keyboard');
    } else if (changes.setFocus && changes.setFocus.currentValue && this.button) {
      setTimeout(() => {
        this.monitor.focusVia(this.button._elementRef, 'keyboard');
      });
    }
  }

  handleBlur(event: Event) {
    this.control.setValue(this.inputControl.value);
    this.control.markAsDirty();
    this.inputControl.updateValueAndValidity({ emitEvent: false, onlySelf: false });
  }

  ngAfterViewChecked() {
    if (this._formGroup && this._formGroup.disabled && this.inputControl.enabled) {
      this.disabled = this._formGroup.disabled;
    } else if (this._formGroup && this._formGroup.enabled && this.inputControl.disabled) {
      this.disabled = this._formGroup.disabled;
      this.inputControl.enable();
      this.inputControl.reset();
    }
    this.cdRef.markForCheck();
  }

  handleDropdown() {
    if (this.matFormField && this.dropdown) {
      setTimeout(() => {
        this._setDropdownWidth();
        this.monitor.focusVia(this.itemInput.nativeElement, 'keyboard');
      });
    }
    this.showError = false;
    this.triggerChange();
  }

  private _setDropdownWidth() {
    if (this.menuElement) {
      this.menuElement.nativeElement.style.width = this.matFormField._elementRef.nativeElement.offsetWidth + 'px';
      this.menuElement.nativeElement.style.maxWidth = 'none';
      this.menuElement.nativeElement.style.minHeight = '60px';
    }
  }

  private _dispatchWindowResizeEvent() {
    window.dispatchEvent(new Event('resize'));
  }

  checkOptionSelected(option: any): boolean {
    const name = option.getName().substr(option.getName().lastIndexOf('/'));
    return this.control.value.some((v: any) => v.getName().indexOf(name) > -1);
  }

  dropdownCheckboxChange($event: MatSelectionListChange) {
    const option = $event.option.value;
    if ($event.option.selected) {
      this.addSelection({ option: { value: option } } as MatAutocompleteSelectedEvent);
    } else {
      const name = option.getName().substr(option.getName().lastIndexOf('/'));
      this.removeSelection(this.control.value.findIndex((v: any) => v.getName().indexOf(name) > -1), false);
    }

    this.triggerChange();

    if (this.matFormField &&
      !this.matFormField._elementRef.nativeElement.classList.contains('mat-focused')) {
      this.matFormField._elementRef.nativeElement.classList.add('mat-focused');
    }

    this.monitor.focusVia(this.itemInput, 'keyboard');
  }

  setFormFieldMatFocusClass() {
    if (this.matFormField) {
      setTimeout(() => {
        this.matFormField._elementRef.nativeElement.classList.add('mat-focused');
      });
    }
  }

  rmFormFieldMatFocusClass($event: FocusEvent) {
    if (this.matFormField &&
      (!$event.relatedTarget || ($event.relatedTarget as HTMLElement).className.indexOf('nebula-menu-inner-input') === -1)) {
      this.matFormField._elementRef.nativeElement.classList.remove('mat-focused');
    }
  }

  focusOnExpand() {
    if (this.matFormField && this.button) {
      this.monitor.focusVia(this.button._elementRef, 'keyboard');
    }
  }

  onListScroll($event: Event) {
    const target = $event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const componentHeight = target.offsetHeight;
    const scrollResetHeight = (scrollHeight - componentHeight) / 2;
    if (!this._scrolling && componentHeight + scrollTop >= scrollHeight) {
      this._loadScrollNextSerie();
    } else if (this.showError && scrollTop <= scrollResetHeight) {
      this._scrolling = false;
      this.showSpinner = false;
      this.showError = false;
    }
  }

  private _loadScrollNextSerie() {
    if (this.dataSource.hasNextPage()) {
      this._scrolling = true;
      this.showSpinner = true;
      this.dataSource.nextPage().pipe(
        take(1),
        catchError((err) => {
          this.showSpinner = false;
          this.showError = true;
          return throwError(err);
        })
      ).subscribe();
    } else {
      this._scrolling = false;
      this.showSpinner = false;
      this.showError = false;
    }
  }

  public handleKeyBoardEvents($event: KeyboardEvent) {
    $event.stopImmediatePropagation();
    // tslint:disable:deprecation
    switch ($event.keyCode) {
      case KeyCode.DOWN_ARROW:
        this.selectableOptionIndex++;
        this._scrollToSelectableOption();
        break;
      case KeyCode.UP_ARROW:
        this.selectableOptionIndex--;
        this._scrollToSelectableOption();
        break;
      case KeyCode.ENTER:
        this._toggleMatListOption();
        break;
      case KeyCode.ESCAPE:
        this.menuTrigger.closeMenu();
        break;
      default:
        break;
    }
    // tslint:enable:deprecation
  }

  private _scrollToSelectableOption() {
    this.selectableOptionIndex = this.selectableOptionIndex < 0 ? 0 : this.selectableOptionIndex;
    if (this.matSelectionList.options.length - 1 < this.selectableOptionIndex) {
      this.selectableOptionIndex = this.matSelectionList.options.length - 1;
    }

    if (this.matSelectionList) {
      const nativeElement = ((this.matSelectionList as any)._element as ElementRef<HTMLElement>).nativeElement;
      const optionHeight = (nativeElement.children[0] as HTMLElement).offsetHeight;
      const listHeight = nativeElement.offsetHeight;
      const scrollTop = nativeElement.scrollTop;

      if (optionHeight * this.selectableOptionIndex > (listHeight - optionHeight + scrollTop)) {
        nativeElement.scrollTop = optionHeight * this.selectableOptionIndex - listHeight + optionHeight;
      } else if (optionHeight * this.selectableOptionIndex < scrollTop) {
        nativeElement.scrollTop = optionHeight * this.selectableOptionIndex;
      } else if (optionHeight * this.selectableOptionIndex === (listHeight + scrollTop - optionHeight)) {
        this._loadScrollNextSerie();
      }
    }
  }

  private _toggleMatListOption() {
    const checkbox = this.matSelectionList.options.find((item, i) => i === this.selectableOptionIndex);
    if (checkbox) {
      checkbox.toggle();
      this.matSelectionList.selectionChange.emit(new MatSelectionListChange(
        this.matSelectionList,
        checkbox
      ));
    }
  }
}
